import { ListItemBox } from '@domains/subscription/priv/components/ListItemBox'
import { StringFormatter } from '@domains/subscription/priv/format'
import { Box, Stack, Typography } from '@mui/material'

export interface SectionRow {
  id: string
  name: string
}
export interface Section {
  index?: number
  title: string
  items: SectionRow[]
  onClick: (id: string) => void
  active: boolean
}

export interface BillingListProps {
  sections: Section[]
  activeRowId?: string
}

const formatter = new StringFormatter({
  normalizers: [
    new StringFormatter.normalizers.camelCase(),
    new StringFormatter.normalizers.enum(),
    new StringFormatter.normalizers.whitespace(),
    new StringFormatter.normalizers.all(),
  ],
  format: new StringFormatter.formats.capitalized(),
})

export const BillingList = ({ sections, activeRowId }: BillingListProps) => {
  const sortedSections = sections
    .filter((section) => section.items.length > 0)
    .toSorted((a, b) => {
      if (a.index !== undefined && b.index !== undefined) {
        return a.index - b.index
      }

      return a.title.localeCompare(b.title)
    })

  return (
    <>
      {sortedSections.map((section) => (
        <Stack
          key={section.title}
          data-testid={`billing-list-section-${section.title}`}
          sx={{
            flex: 1,
          }}
        >
          <Box
            sx={{
              mb: 2,
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: 'text.primary',
              }}
            >
              {section.title}
            </Typography>
          </Box>
          <Stack
            sx={{
              gap: 1,
            }}
          >
            <BillingItem items={section.items} focusedId={activeRowId} active={section.active} onClick={(id) => section.onClick(id)} />
          </Stack>
        </Stack>
      ))}
    </>
  )
}

interface BillingItemProps {
  items: SectionRow[]
  focusedId: string | undefined
  active: boolean
  onClick: (id: string) => void
}

const BillingItem = ({ items, focusedId, active, onClick }: BillingItemProps) => {
  if (items.length === 0) return null

  const sortedItems = items.toSorted((a, b) => a.name.localeCompare(b.name))

  return (
    <>
      {sortedItems.map((item) => (
        <ListItemBox
          focused={focusedId === item.id}
          active={active}
          key={item.id}
          title={formatter.format(item.name)}
          onClick={() => onClick(item.id)}
        />
      ))}
    </>
  )
}
